import {
  generateChartData,
  type ChartDataPoint,
} from '@/utils/generate-chart-data'
import type { ActivityReportResponse } from '@/lib/api/reporting.client'

export function buildChartData(
  activityReport: ActivityReportResponse | null,
  dateRange: string,
): ChartDataPoint[] {
  if (!activityReport?.dataPoints) {
    return generateChartData(dateRange)
  }

  const days = activityReport.dataPoints.length

  const sortedDataPoints = [...activityReport.dataPoints].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  // Keep only the first point per date so duplicate dates don't produce duplicate lines.
  const dateMap = new Map<string, (typeof sortedDataPoints)[0]>()
  for (const point of sortedDataPoints) {
    const dateKey =
      typeof point.date === 'string' && point.date.match(/^\d{4}-\d{2}-\d{2}/)
        ? point.date
        : new Date(point.date).toISOString().split('T')[0]

    if (!dateMap.has(dateKey)) {
      dateMap.set(dateKey, point)
    }
  }

  const uniqueDataPoints = Array.from(dateMap.entries())
    .sort(([dateKeyA], [dateKeyB]) => dateKeyA.localeCompare(dateKeyB))
    .map(([, point]) => point)

  const chartDataPoints = uniqueDataPoints.map((point, index) => {
    let date: Date
    if (
      typeof point.date === 'string' &&
      point.date.match(/^\d{4}-\d{2}-\d{2}/)
    ) {
      const [year, month, day] = point.date.split('-').map(Number)
      date = new Date(year, month - 1, day)
    } else {
      date = new Date(point.date)
    }

    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', point.date)
      date = new Date()
    }

    const dateKey =
      typeof point.date === 'string'
        ? point.date
        : date.toISOString().split('T')[0]

    let dateLabel = ''
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const day = date.getDate()

    if (days <= 7) {
      dateLabel = `${month} ${day}`
    } else if (days <= 30) {
      const sortedUniqueDates = Array.from(dateMap.keys()).sort()
      const dateIndex = sortedUniqueDates.indexOf(dateKey)
      if (dateIndex % 5 === 0 || dateIndex === sortedUniqueDates.length - 1) {
        dateLabel = `${month} ${day}`
      }
    } else {
      const sortedUniqueDates = Array.from(dateMap.keys()).sort()
      const dateIndex = sortedUniqueDates.indexOf(dateKey)
      if (dateIndex % 7 === 0 || dateIndex === sortedUniqueDates.length - 1) {
        dateLabel = `${month} ${day}`
      }
    }

    const uniqueDateId = dateKey || date.toISOString().split('T')[0]

    return {
      date: uniqueDateId,
      dateLabel,
      fullDate: date,
      timestamp: date.getTime(),
      callsHandledByAI: point.callsHandled,
      callsOutbound: point.callsHandledOutbound,
      callsResolved: point.callsResolved,
      smsHandledByAI: point.smsHandled,
      smsResolved: point.smsResolved,
      _index: index,
      _dateKey: dateKey,
    }
  })

  // Recharts positions points by array order, so this must be sorted chronologically.
  return [...chartDataPoints].sort((a, b) => a.timestamp - b.timestamp)
}
